import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { AUTHENTICATOR_OTP } from "../../config/constants";
import { getFieldErrors, IFieldError } from "../helpers";
import { clearLoginErrs, IAuthenticatorSecret } from "./authReducer";

class ReqAuthenticatorSetupErrs {
    form: string | null = null;
    errCode: string | null = null;
    email: string | null = null;
}

export interface IAuthenticatorState {
    requestingAuthenticatorSetup: boolean;
    requestedAuthenticatorSetup: boolean;
    reqAuthenticatorSetupErrs: ReqAuthenticatorSetupErrs;
    fetchingAuthenticatorSecret: Boolean;
    authenticatorSecret: IAuthenticatorSecret | null;
    fetchAuthenticatorSecretErr: string | null;
    confirmingAuthenticatorSetup: boolean;
    confirmedAuthenticatorSetup: boolean;
    confirmAuthenticatorSetupErr: string | null;
}

const initialState = {
    requestingAuthenticatorSetup: false,
    requestedAuthenticatorSetup: false,
    reqAuthenticatorSetupErrs: new ReqAuthenticatorSetupErrs(),
    fetchingAuthenticatorSecret: false,
    authenticatorSecret: null,
    fetchAuthenticatorSecretErr: null,
    confirmingAuthenticatorSetup: false,
    confirmedAuthenticatorSetup: false,
    confirmAuthenticatorSetupErr: null,
} as IAuthenticatorState;


const authenticatorSlice = createSlice({
    name: "authenticator",
    initialState,
    reducers: {
        clearReqAuthenticatorSetupFieldErr: (state: IAuthenticatorState, action: PayloadAction<string>) => {
            state.reqAuthenticatorSetupErrs[action.payload as keyof ReqAuthenticatorSetupErrs] = null;
        },
        clearReqAuthenticatorSetupErrs: (state: IAuthenticatorState) => {
            state.reqAuthenticatorSetupErrs = new ReqAuthenticatorSetupErrs();
        },
        tryResetReqAuthenticatorSetupFormErr: (state: IAuthenticatorState) => {
            let shouldReset = true;

            for (let key in state.reqAuthenticatorSetupErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.reqAuthenticatorSetupErrs[key as keyof ReqAuthenticatorSetupErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.reqAuthenticatorSetupErrs.form = null;
            }
        },
        resetRequestedAuthenticatorSetup: (state: IAuthenticatorState) => {
            state.requestedAuthenticatorSetup = false;
        },
        reqAuthenticatorSetupStart: (state: IAuthenticatorState) => {
            state.reqAuthenticatorSetupErrs = new ReqAuthenticatorSetupErrs();
            state.requestedAuthenticatorSetup = false;
            state.requestingAuthenticatorSetup = true;
        },
        reqAuthenticatorSetupPass: (state: IAuthenticatorState) => {
            state.requestingAuthenticatorSetup = false;
            state.requestedAuthenticatorSetup = true;
        },
        reqAuthenticatorSetupFail: (state: IAuthenticatorState, action: PayloadAction<{ errs: IFieldError[]; errCode?: string; }>) => {
            state.requestingAuthenticatorSetup = false;

            const { errs, errCode } = action.payload;

            if (errCode) {
                state.reqAuthenticatorSetupErrs.errCode = errCode;
            }

            for (const err of errs) {
                for (const key in state.reqAuthenticatorSetupErrs) {
                    if (err.field === key) {
                        state.reqAuthenticatorSetupErrs[key as keyof ReqAuthenticatorSetupErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.reqAuthenticatorSetupErrs.form) {
                state.reqAuthenticatorSetupErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchAuthenticatorSecretErr: (state: IAuthenticatorState) => {
            state.fetchAuthenticatorSecretErr = null;
        },
        clearAuthenticatorSecret: (state: IAuthenticatorState) => {
            state.authenticatorSecret = null;
        },
        fetchAuthenticatorSecretStart: (state: IAuthenticatorState) => {
            state.fetchAuthenticatorSecretErr = null;
            state.authenticatorSecret = null;
            state.fetchingAuthenticatorSecret = true;
        },
        fetchAuthenticatorSecretPass: (state: IAuthenticatorState, action: PayloadAction<IAuthenticatorSecret>) => {
            state.authenticatorSecret = action.payload;
            state.fetchingAuthenticatorSecret = false;
        },
        fetchAuthenticatorSecretFail: (state: IAuthenticatorState, action: PayloadAction<string>) => {
            state.fetchAuthenticatorSecretErr = action.payload;
            state.fetchingAuthenticatorSecret = false;
        },
        clearConfirmAuthenticatorSetupErr: (state: IAuthenticatorState) => {
            state.confirmAuthenticatorSetupErr = null;
        },
        resetConfirmedAuthenticatorSetup: (state: IAuthenticatorState) => {
            state.confirmedAuthenticatorSetup = false;
        },
        confirmAuthenticatorSetupStart: (state: IAuthenticatorState) => {
            state.confirmedAuthenticatorSetup = false;
            state.confirmAuthenticatorSetupErr = null;
            state.confirmingAuthenticatorSetup = true;
        },
        confirmAuthenticatorSetupPass: (state: IAuthenticatorState) => {
            state.confirmedAuthenticatorSetup = true;
            state.confirmingAuthenticatorSetup = false;
        },
        confirmAuthenticatorSetupFail: (state: IAuthenticatorState, action: PayloadAction<string>) => {
            state.confirmAuthenticatorSetupErr = action.payload;
            state.confirmingAuthenticatorSetup = false;
        },
    }
});

export const {
    clearReqAuthenticatorSetupFieldErr,
    clearReqAuthenticatorSetupErrs,
    tryResetReqAuthenticatorSetupFormErr,
    resetRequestedAuthenticatorSetup,
    reqAuthenticatorSetupStart,
    reqAuthenticatorSetupPass,
    reqAuthenticatorSetupFail,
    clearFetchAuthenticatorSecretErr,
    clearAuthenticatorSecret,
    fetchAuthenticatorSecretStart,
    fetchAuthenticatorSecretPass,
    fetchAuthenticatorSecretFail,
    clearConfirmAuthenticatorSetupErr,
    resetConfirmedAuthenticatorSetup,
    confirmAuthenticatorSetupStart,
    confirmAuthenticatorSetupPass,
    confirmAuthenticatorSetupFail,
} = authenticatorSlice.actions;

interface ITryReqAuthenticatorSetupArg {
    email: string;
}

export const tryReqAuthenticatorSetup = createAsyncThunk("auth/tryReqAuthenticatorSetup", (arg: ITryReqAuthenticatorSetupArg, { dispatch }) => {
    const { email } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/email-user-2-goto-authenticator-setup-page`;

    dispatch(reqAuthenticatorSetupStart());

    axios.post(endpoint, { email }, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(reqAuthenticatorSetupPass(res.data));
        dispatch(clearLoginErrs());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];
        let errCode: string | undefined = undefined;

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];
            if (data?.errCode) {
                errCode = data.errCode;
            }

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(reqAuthenticatorSetupFail({ errs: fieldErrors, errCode }));
    });
});

interface ITryFetchAuthenticatorSecretArg {
    emailToken: string;
}

export const tryFetchAuthenticatorSecret = createAsyncThunk("auth/tryFetchAuthenticatorSecret", (arg: ITryFetchAuthenticatorSecretArg, { dispatch }) => {
    const { emailToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/authenticator-secret?emailedToken=${emailToken}`;

    dispatch(fetchAuthenticatorSecretStart());
    axios.get(endpoint, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(fetchAuthenticatorSecretPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;
        let error = '';

        if (data?.errMsg && typeof data?.errMsg === typeof new String('')) {
            error = data.errMsg;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(fetchAuthenticatorSecretFail(error));
    });

});

interface ITryConfirmAuthenticatorSetup {
    emailToken?: string;
    authToken?: string;
    otp: string;
}

export const tryConfirmAuthenticatorSetup = createAsyncThunk("auth/tryConfirmAuthenticatorSetup", (arg: ITryConfirmAuthenticatorSetup, { dispatch }) => {
    const { emailToken, otp, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/${emailToken ? "confirm-authenticator-setup" : "confirm-auth-user-authenticator-setup"}`;

    dispatch(confirmAuthenticatorSetupStart());
    setTimeout(() => {
        axios.post(endpoint, emailToken ? { emailToken, otp, type: AUTHENTICATOR_OTP } : { otp }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": authToken ? `Bearer ${authToken}` : undefined
            }
        }).then(res => {
            dispatch(confirmAuthenticatorSetupPass());
        }).catch(err => {
            const data = err?.response?.data;
            let error = '';

            if (data?.errMsg) {
                error = data.errMsg;
            } else if (data?.errors) {
                error = getFieldErrors(data.errors)[0].msg;
            } else {
                error = 'Check your network connection.';
            }

            dispatch(confirmAuthenticatorSetupFail(error));
        });
    }, 1000);
});

export default authenticatorSlice.reducer;
